import AuthenticatedImage from '../../Items/AuthenticatedImage';
import CloseButton from 'src/components/Buttons/CloseButton';
import DateFormat from '../../../utils/dateFormat';
import Modal from '../../../utils/modal';
import moment from '../../../utils/moment';
import NormalButton from '../../Buttons/NormalButton';
import React, { useCallback, useMemo } from 'react';
import SVG from '../../Images/SvgRenderer';
import useStyles from './style';
import { createNotification } from '../../../utils/createNotification';
import { addSubstitutionsList, deleteWatchdog } from '../../../store/actions/substitutions.actions';
import { isCypress } from '../../../utils/useCypress';
import { findMin, handleSum, isKey } from 'src/utils/useFunctions';
import { setWatchdogDetailModal } from '../../../store/actions/modals.actions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useEffect } from 'src/utils/useEffect';
import { useStates } from '../../../utils/useState';
import { useTranslation } from 'react-i18next';

const WatchdogDetailModal: React.FunctionComponent = () => {

  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const dataData = useAppSelector((state: any) => state.data);
  const substitutionsData = useAppSelector((state: any) => state.substitutions);
  const languageData = useAppSelector((state: any) => state.language);
  const modalsData = useAppSelector((state: any) => state.modals);
  const classService = useAppSelector((state: any) => state.services).classService;
  const substitutionsService = useAppSelector((state: any) => state.services).substitutionsService;
  const watchdogService = useAppSelector((state: any) => state.services).watchdogService;

  const [state, setState] = useStates({
    canClick: true,
    isCustom: false,
    customData: [],
    isReady: false,
    customClassesData: [],
    isLoading: true,
  });
  
  const watchdogID = modalsData.watchdogDetailModal.watchdogID;
  const childID = modalsData.watchdogDetailModal.childID;
  const readOnly = modalsData.watchdogDetailModal.readOnly;
  const list = substitutionsData.watchdogs;
  const watchdogDataAll = useMemo(() => { return state.isCustom ? state.customData : (list.length > 0 && childID !== null && childID !== undefined) ? (list.filter((data: any) => data.childID === childID).length > 0 ? list.find((data: any) => data.childID === childID).dates : []) : []; }, [childID, list, state.customData, state.isCustom]);
  const watchdogData = useMemo(() => { return watchdogDataAll.length > 0 ? (watchdogDataAll.filter((data: any) => data.watchdogID === watchdogID).length === 0 ? {} : watchdogDataAll.find((data: any) => data.watchdogID === watchdogID)) : {}; }, [watchdogDataAll, watchdogID]);
  const authorData = dataData.users.find((user: any) => user.userID === watchdogData.authorID);
  const curClassID = watchdogData.classID;
  const watchdogDate = watchdogData.date;
  const watchdogDayPart = watchdogData.dayPart;
  const classData = curClassID ? dataData.classes.find((theClass: any) => theClass.classID === curClassID) : [];

  const currentClassCapacity: any = () => {
    if(curClassID && state.customClassesData.length !== 0) {
      const getCustomClassData = (classID: any) => {
        return state.customClassesData.filter((item: any) => item.classID === classID).length === 0 ? {} : state.customClassesData.find((item: any) => item.classID === classID);
      };
      const data = {
        morningCapacity: getCustomClassData(curClassID).availableMorningCount,
        afternoonCapacity: getCustomClassData(curClassID).availableAfternoonCount,
        wholeDayCapacity: findMin(getCustomClassData(curClassID).availableMorningCount, getCustomClassData(curClassID).availableAfternoonCount),
      };
      return data;
    } else {
      return null;
    }
  };

  const getCurrentPartOfDayCapacity = () => {
    const partOfDay = parseInt(watchdogDayPart);
    if(partOfDay === 1) return currentClassCapacity() ? currentClassCapacity().wholeDayCapacity : 0;
    else if(partOfDay === 2) return currentClassCapacity() ? currentClassCapacity().morningCapacity : 0;
    else if(partOfDay === 3) return currentClassCapacity() ? currentClassCapacity().afternoonCapacity : 0;
    else return 0;
  };

  const getAttendanceDayPart = () => {
    const partOfDay = parseInt(watchdogDayPart);
    if(partOfDay === 1) return "all";
    else if(partOfDay === 2) return "am";
    else if(partOfDay === 3) return "pm";
    else return "all";
  };

  const getDateCredit = () => {
    const allDays = substitutionsData.childData.allDays;
    const credits = allDays.filter((item: any) => item.substitutionRemaining && moment(item.date).isBetween(moment(watchdogDate).subtract(substitutionsData.childData.schoolDays, 'days'), moment(watchdogDate).add(substitutionsData.childData.schoolDays, 'days')) && moment(item.date).isBefore(moment(watchdogDate),'day')).map((item: any) => {
      return item.substitutionRemaining;
    }).reduce(handleSum, 0);
    return credits;
  };

  const getChildData = (childID: any) => {
    return dataData.children.filter((item: any) => item.childID === childID).length === 0 ? [] : dataData.children.find((item: any) => item.childID === childID);
  };

  const parseDay = (day: any) => {
    if(day === 0) return 7;
    else return day;
  };

  const childData = getChildData(childID);
  const canCreateSubstitution = childID && !childData.isArchived && childData.isInAnyActiveClass && moment(watchdogData.date).isSameOrAfter(moment()) && getDateCredit() > 0 && (substitutionsData.childData.attendanceProgram.days.filter((day: any) => day.day === parseDay(moment(watchdogDate).day())).length === 0 || (substitutionsData.childData.attendanceProgram.days.filter((day: any) => day.day === parseDay(moment(watchdogDate).day())).length !== 0 && substitutionsData.childData.attendanceProgram.days.find((day: any) => day.day === parseDay(moment(watchdogDate).day())).attendanceType !== getAttendanceDayPart())) && substitutionsData.childData.workingDays.indexOf(parseDay(moment(watchdogDate).day())) !== -1 && substitutionsData.childData.holidayDates.indexOf(parseDay(moment(watchdogDate).day())) === -1 && substitutionsData.childData.allDates.indexOf(parseDay(moment(watchdogDate).day())) === -1 && getCurrentPartOfDayCapacity() > 0;

  const onCloseModal = useCallback(() => {
    const settings = {
      isOpen: false,
      date: null,
      watchdogID: null,
      childID: null,
      readOnly: false,
    };
    dispatch(setWatchdogDetailModal(settings));
  }, [dispatch]);

  useEffect(() => {
    if(Object.keys(watchdogData).length === 0) {
      watchdogService && watchdogService.findWatchdog(watchdogID).then((result: any) => {
        if(result) {
          if(result.data) {
            if(result.data.length === 1) {
              if(result.data[0].childID === childID) {
                setState("customData", result.data);
                setState("isCustom", true);
                setState("isReady", true);
              } else {
                createNotification(t("watchdog_failed_load"), "error");
                onCloseModal();
              }
            } else {
              createNotification(t("watchdog_failed_load"), "error");
              onCloseModal();
            }
          } else {
            createNotification(t("watchdog_failed_load"), "error");
            onCloseModal();
          }
        } else {
          createNotification(t("watchdog_failed_load"), "error");
          onCloseModal();
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("watchdog_failed_load"), "error");
        onCloseModal();
      });
    } else {
      setState("isReady", true);
    }
  }, [onCloseModal, setState, watchdogData, watchdogID, watchdogService, t], []);

  useEffect(() => {
    if(moment(watchdogData.date).isSameOrAfter(moment())) {
      classService && classService.listClassCapacity(moment(watchdogData.date).format("YYYY-MM-DD")).then((result: any) => {
        if(result) {
          if(result.data) {
            if(result.data.classes) {
              const classesData = result.data.classes;
              setState("customClassesData", classesData);
              setState("isLoading", false);
            } else {
              createNotification(t("class_capacity_failed_load"), "error");
            }
          } else {
            createNotification(t("class_capacity_failed_load"), "error");
          }
        } else {
          createNotification(t("class_capacity_failed_load"), "error");
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("class_capacity_failed_load"), "error");
      });
    }
  }, [], [watchdogData.date]);

  const handleClose = (e: any) => {
    e.stopPropagation();
    onCloseModal();
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    if(state.canClick) {
      setState("canClick", false);
      watchdogService && watchdogService.deleteWatchdog(watchdogData.watchdogID).then((result: any) => {
        if(result.status === 204) {
          createNotification(t("watchdog_deleted"),"success");
          const settings = {
            watchdogID: watchdogData.watchdogID,
            childID: childData.childID,
          };
          dispatch(deleteWatchdog(settings));
          setState("canClick", true);
          onCloseModal();      
        } else {
          createNotification(t("watchdog_not_deleted"),"error");
          setState("canClick", true);
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.message) ? e.response.message : t("watchdog_not_deleted"),"error");
        setState("canClick", true);
      });
    }
  };

  const handleCreateSubstitution = (e: any) => {
    if(!state.canClick) return;
    setState("canClick", false);
    e.stopPropagation();
    if(moment(state.startDate).isSameOrAfter(moment(),'day') && moment(state.startDate).isSameOrBefore(moment().add(180,'days'),'day') && moment(state.endDate).isSameOrAfter(moment(),'day') && moment(state.endDate).isSameOrBefore(moment().add(180,'days'),'day')) {
      const payload = {
        childrenID: [childID],
        classID: curClassID,
        dateStart: moment(watchdogDate).format("YYYY-MM-DD"),
        dateEnd: moment(watchdogDate).format("YYYY-MM-DD"),
        substitutionPartID: parseInt(watchdogDayPart),
      };
      substitutionsService && substitutionsService.createSubstitution(payload).then((result: any) => {
        if(result.status === 200) {
          createNotification(t("substitution_added"), "success");
          dispatch(addSubstitutionsList(result.data.substitution));
          const settings = {
            watchdogID: watchdogID,
            childID: childID,
          };
          dispatch(deleteWatchdog(settings));
          setState("canClick", true);
          onCloseModal();
        } else {
          createNotification(t("substitution_not_added"), "error");  
          setState("canClick", true);
        }
      }).catch((e: any) => {
        createNotification(!isKey(e.response.data.message) ? e.response.data.message : t("substitution_not_added"), "error");
        setState("canClick", true);
      });
    } else {
      createNotification(t("substitution_not_added_past"), "error");
      setState("canClick", true);
    }
  };

  return state.isReady ? (
    <Modal 
      open={true}
      onClose={onCloseModal}
    >
      {
        Object.keys(watchdogData).length > 0 ? (
          <div className={classes.root} data-cy={isCypress() ? "watchdogDetailModal" : null}>
            <div className={classes.header}>
              <div className={classes.wrapper}>
                <p>
                  {t('watchdog_detail')}
                  &nbsp;
                  {DateFormat(moment(watchdogData.date),"default", languageData, t)}
                  </p>
              </div>
              <CloseButton onClick={handleClose} dataCy="timesButton"/>
            </div>
            <div className={classes.body}>
              <div className={classes.item}>
                <span className={classes.title}>
                  <SVG src="user-circle-outlined"/>
                  {t('child')}
                </span>
                <div className={classes.childPhoto}>
                  <AuthenticatedImage thumbLink={childData.photo?.thumbLink}/>
                  <p className={classes.nickname}>{childData.displayName}</p>
                </div>
              </div>
              {
                curClassID ? (
                  <div className={classes.item}>
                    <span className={classes.title}>
                      <SVG src="class"/>
                      {t('class')}
                    </span>
                    <p className={classes.detail}>
                      {classData.name}
                    </p>
                  </div>
                ) : null
              }
              <div className={classes.item}>
                <p className={classes.title}>
                  <SVG src="calendar"/>
                  {t('date')}
                </p>
                <p className={classes.detail}>
                  {DateFormat(moment(watchdogData.date),"default", languageData, t)}
                </p>
              </div>
              <div className={classes.item}>
                <p className={classes.title}>
                  <SVG src="clock"/>
                  {t('part_of_day')}
                </p>
                <p className={classes.detail}>
                  {t(watchdogData.dayPart === 1 ? "whole_day" : watchdogData.dayPart === 2 ? "morning" : "afternoon" )}
                </p>
              </div>
              <div className={classes.footerInfo}>
                <p>{t('author')}: {authorData.displayName}</p>
                <p>{t('created')}: {DateFormat(moment(watchdogData.createdDate),"daymonthname", languageData, t)} &bull; {DateFormat(moment(watchdogData.createdDate),"time", languageData, t)}</p>
              </div>
            </div>
            {
              (!readOnly) ? (
                <div className={classes.footer}>
                  <div>
                    {
                      state.isLoading ? (
                        <>
                          {
                            moment(watchdogData.date).isSameOrAfter(moment()) ? (
                              <NormalButton buttonType="primary" disabled={true} dataCy="deleteWatchdogButton">
                                {t("delete_watchdog")}
                              </NormalButton>
                            ) : null
                          }
                        </>
                      ) : (
                        <>
                          {
                            (moment(watchdogData.date).isSameOrAfter(moment()) && !childData.isArchived) ? (
                              <NormalButton buttonType={canCreateSubstitution ? "secondary" : "primary"} onClick={handleDelete} disabled={!state.canClick} dataCy="deleteWatchdogButton">
                                {t("delete_watchdog")}
                              </NormalButton>
                            ) : null
                          }
                          {
                            canCreateSubstitution ? (
                              <NormalButton buttonType="primary" onClick={handleCreateSubstitution} disabled={!state.canClick} dataCy="createSubstitutionButton">
                                {t("create_substitution_from_watchdog")}
                              </NormalButton>
                            ) : null
                          }
                        </>
                      )
                    }
                  </div>
                </div>
              ) : (
              <div className={classes.footer}/>
              )
            }
          </div>
        ) : (
          <></>
        )
      }
    </Modal>
  ) : null;
};

export default WatchdogDetailModal;